import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const T = {
  bg: '#ffffff',
  bg2: '#fafafa',
  bg3: '#f4f4f5',
  text: '#09090b',
  text2: '#52525b',
  text3: '#a1a1aa',
  border: '#e4e4e7',
  accent: '#6366f1',
  accentHover: '#4f46e5',
  accentLight: 'rgba(99,102,241,0.08)',
  accentBorder: 'rgba(99,102,241,0.25)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
}

type ChangeType = 'new' | 'improved' | 'fixed' | 'breaking'

interface Change {
  type: ChangeType
  text: string
}

interface Release {
  version: string
  date: string
  title: string
  changes: Change[]
}

const BADGE: Record<ChangeType, { label: string; color: string; bg: string; border: string }> = {
  new:      { label: 'Новое',    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  improved: { label: 'Улучшено', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
  fixed:    { label: 'Исправлено', color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  breaking: { label: 'Важно',    color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
}

const RELEASES: Release[] = [
  {
    version: '0.9.0',
    date: '10 августа 2026',
    title: 'Мобильный редактор, AI-ассистент 2.0, Витрина',
    changes: [
      { type: 'new', text: 'Мобильный редактор: TouchSensor drag-and-drop, нижняя панель блоков, таб-бар навигации' },
      { type: 'new', text: 'AI-ассистент 2.0: умное приветствие с анализом страницы, быстрые действия (секция, текст, тема)' },
      { type: 'new', text: 'Витрина BST: галерея публичных сайтов с фильтрацией по тегам и поиском' },
      { type: 'new', text: 'Страница changelog доступна по /changelog' },
      { type: 'improved', text: 'AI-чат: генерация цветовых тем с предпросмотром свотчей, копирование hex одним кликом' },
      { type: 'improved', text: 'AI-чат: генерация копирайта с кнопкой «Скопировать»' },
      { type: 'improved', text: 'Автоматическое открытие панели Свойств при выборе блока на мобильном устройстве' },
    ],
  },
  {
    version: '0.8.0',
    date: '8 августа 2026',
    title: 'StyleVariants для шаблонов, CircularDep fix, 9 стилей лендинга',
    changes: [
      { type: 'new', text: 'StyleSwitcher в настройках сайта — 9 готовых стилей для лендинга (Minimal, Corporate, Dark, Vibrant, Pastel, Retro, Nature, Luxury, Cinematic)' },
      { type: 'new', text: '8 стилей для портфолио с поддержкой StyleVariants' },
      { type: 'improved', text: 'Шаблон портфолио вынесен в отдельный helpers-файл, устранена circular dependency' },
      { type: 'fixed', text: 'CORE brand блок: неверный цвет кнопки на тёмном фоне' },
      { type: 'fixed', text: 'BENTO кнопка входа: неправильный z-index при оверлее' },
      { type: 'fixed', text: 'StyleSwitcher: templateId не обновлялся при переключении стиля' },
    ],
  },
  {
    version: '0.7.0',
    date: '6 августа 2026',
    title: 'Портфолио-рендереры, 17 блоков в Properties panel',
    changes: [
      { type: 'new', text: '17 новых блоков получили редактируемые свойства в Properties panel: Portfolio Grid, Portfolio Masonry, Portfolio Carousel, Project Detail и другие' },
      { type: 'new', text: 'Портфолио-рендереры: Photo Wall, Horizontal Scroll, Timeline, Side-by-Side Layout' },
      { type: 'improved', text: 'Drag-and-drop шаблон PORTFOLIO теперь корректно прокидывает данные секций' },
      { type: 'fixed', text: 'Некорректное связывание блоков PORTFOLIO_TEMPLATE при создании сайта из шаблона' },
    ],
  },
  {
    version: '0.6.0',
    date: '3 августа 2026',
    title: 'Auth-Form блок, личный кабинет посетителя, хостинговый план',
    changes: [
      { type: 'new', text: 'Блок Auth-Form: регистрация и вход посетителей прямо на опубликованном сайте' },
      { type: 'new', text: 'Личный кабинет посетителя: просмотр и редактирование профиля, смена пароля' },
      { type: 'new', text: 'Хостинговый план: загрузка произвольного HTML — BST становится хостингом для готового кода' },
      { type: 'new', text: 'Эндпоинт публичных пользователей сайта (/public/site-users/:siteId)' },
      { type: 'improved', text: 'htmlExport поддерживает auth-form — виджет JS встраивается в статический экспорт' },
      { type: 'fixed', text: 'Prisma migration DLL workaround для Windows окружений' },
    ],
  },
  {
    version: '0.5.0',
    date: '2 августа 2026',
    title: 'Командная работа, Newsletter, YooKassa, Analytics',
    changes: [
      { type: 'new', text: 'Командная работа: приглашение по email, роли Editor/Viewer, управление участниками' },
      { type: 'new', text: 'Newsletter: подписка, отписка, история кампаний, экспорт CSV' },
      { type: 'new', text: 'Платежи YooKassa: webhook, подтверждение оплаты, Trial-период' },
      { type: 'new', text: 'Domain verification: автоматическая проверка DNS TXT/CNAME записей' },
      { type: 'new', text: 'Performance indexes на Prisma-уровне для ускорения запросов' },
      { type: 'improved', text: 'Dashboard UI: sticky preview panel, slide-in анимации' },
      { type: 'fixed', text: '"Сервер недоступен" — проблема с JWT и refresh-токенами в production-режиме' },
    ],
  },
  {
    version: '0.4.0',
    date: '1 августа 2026',
    title: 'CMS, E-commerce, A/B тесты, Webhooks',
    changes: [
      { type: 'new', text: 'Headless CMS с коллекциями и динамическими страницами' },
      { type: 'new', text: 'E-commerce: каталог, корзина, заказы, промокоды, цифровые товары, ЮКасса' },
      { type: 'new', text: 'A/B тесты: сравнение вариантов блоков с накоплением статистики' },
      { type: 'new', text: 'Inbound webhooks: получение данных из Zapier, Make и других сервисов' },
      { type: 'new', text: 'API ключи в dashboard для сторонних интеграций' },
      { type: 'new', text: 'Экспорт сайта как ZIP (статический HTML) и JSON-бэкап' },
      { type: 'new', text: 'Мультиязычность: переводы на 15 языков, встроенный языковой переключатель' },
    ],
  },
  {
    version: '0.3.0',
    date: '29 июля 2026',
    title: '376+ блоков, 29 шаблонов, кастомные блоки',
    changes: [
      { type: 'new', text: '376+ готовых блоков во всех категориях: слайдеры, таймеры, квизы, калькуляторы, карты, анимации, параллакс' },
      { type: 'new', text: '29 готовых шаблонов сайтов: Landing, Portfolio, Restaurant, Agency, Startup и другие' },
      { type: 'new', text: 'Кастомные блоки: создание собственных блоков через CRUD-интерфейс' },
      { type: 'new', text: 'History: версионирование страниц с восстановлением любой версии' },
      { type: 'new', text: 'Scheduling: автопубликация в заданное время' },
      { type: 'new', text: 'Clone site, Transfer ownership, Site password protection' },
    ],
  },
  {
    version: '0.1.0',
    date: '20 июля 2026',
    title: 'Публичная бета',
    changes: [
      { type: 'new', text: 'Визуальный drag-and-drop редактор на базе @dnd-kit' },
      { type: 'new', text: 'AI-генерация секций, копирайтинг, подбор темы' },
      { type: 'new', text: 'Публикация сайта на поддомене .mybst.ru с SSL' },
      { type: 'new', text: 'Кастомный домен с автоматическим Let\'s Encrypt сертификатом' },
      { type: 'new', text: 'SEO-настройки: мета-теги, Open Graph, robots.txt, Sitemap' },
      { type: 'new', text: 'Renderer на Next.js 15 с SSR и Core Web Vitals оптимизацией' },
    ],
  },
]

function ReleaseBadge({ type }: { type: ChangeType }) {
  const b = BADGE[type]
  return (
    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: b.color, background: b.bg, border: `1px solid ${b.border}`, padding: '2px 7px', borderRadius: 20, flexShrink: 0 }}>
      {b.label}
    </span>
  )
}

function ReleaseCard({ release }: { release: Release }) {
  const isLatest = release === RELEASES[0]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 40, paddingBottom: 56, borderBottom: `1px solid ${T.border}` }}>
      {/* Left: version + date */}
      <div style={{ paddingTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 18, fontWeight: 900, color: T.text, letterSpacing: -0.5 }}>v{release.version}</span>
          {isLatest && (
            <span style={{ fontSize: 10, fontWeight: 700, color: T.accent, background: T.accentLight, border: `1px solid ${T.accentBorder}`, padding: '2px 7px', borderRadius: 20 }}>Latest</span>
          )}
        </div>
        <div style={{ fontSize: 13, color: T.text3 }}>{release.date}</div>
      </div>

      {/* Right: title + changes */}
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text, letterSpacing: -0.3, marginBottom: 20, lineHeight: 1.3 }}>
          {release.title}
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {release.changes.map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <ReleaseBadge type={c.type} />
              <span style={{ fontSize: 14, color: T.text2, lineHeight: 1.6 }}>{c.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function ChangelogPage() {
  const navigate = useNavigate()

  useEffect(() => {
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    document.body.style.height = 'auto'
    document.documentElement.classList.remove('dark')
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.height = ''
    }
  }, [])

  return (
    <div style={{ background: T.bg, color: T.text, fontFamily: T.font, minHeight: '100vh' }}>
      <style>{`* { box-sizing: border-box; margin: 0; padding: 0; } a { text-decoration: none; color: inherit; }`}</style>

      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${T.border}` }}>
        <div style={{ maxWidth: 960, margin: '0 auto', height: 60, display: 'flex', alignItems: 'center', gap: 16, padding: '0 24px' }}>
          <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: T.font }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: '#fff', fontSize: 13 }}>B</div>
            <span style={{ fontWeight: 900, fontSize: 18, letterSpacing: -0.5, color: T.text }}>BST</span>
          </button>
          <span style={{ color: T.border, fontSize: 18 }}>/</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: T.text2 }}>Changelog</span>
          <div style={{ flex: 1 }} />
          <button onClick={() => navigate('/showcase')} style={{ fontSize: 13, color: T.text2, background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontFamily: T.font, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.accentBorder; e.currentTarget.style.color = T.accent }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.text2 }}
          >
            Витрина →
          </button>
          <button onClick={() => navigate('/login')} style={{ fontSize: 13, fontWeight: 700, color: '#fff', background: T.accent, border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontFamily: T.font, transition: 'background 0.15s' }}
            onMouseEnter={e => (e.currentTarget.style.background = T.accentHover)}
            onMouseLeave={e => (e.currentTarget.style.background = T.accent)}
          >
            Попробовать →
          </button>
        </div>
      </header>

      {/* Hero */}
      <section style={{ padding: '64px 24px 56px', borderBottom: `1px solid ${T.border}`, background: T.bg2 }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.accent, background: T.accentLight, border: `1px solid ${T.accentBorder}`, padding: '4px 12px', borderRadius: 20, marginBottom: 16 }}>
            История версий
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 900, letterSpacing: -1.5, color: T.text, marginBottom: 14, lineHeight: 1.1 }}>
            Changelog
          </h1>
          <p style={{ fontSize: 17, color: T.text2, lineHeight: 1.65, maxWidth: 520 }}>
            Все обновления BST в одном месте. Мы постоянно улучшаем платформу на основе обратной связи.
          </p>
        </div>
      </section>

      {/* Legend */}
      <div style={{ borderBottom: `1px solid ${T.border}`, padding: '16px 24px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {(Object.entries(BADGE) as Array<[ChangeType, typeof BADGE[ChangeType]]>).map(([, b]) => (
            <span key={b.label} style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: b.color, background: b.bg, border: `1px solid ${b.border}`, padding: '3px 9px', borderRadius: 20 }}>
              {b.label}
            </span>
          ))}
        </div>
      </div>

      {/* Releases */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '56px 24px', display: 'flex', flexDirection: 'column', gap: 56 }}>
        {RELEASES.map(release => (
          <ReleaseCard key={release.version} release={release} />
        ))}
      </div>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${T.border}`, padding: '32px 24px', textAlign: 'center', background: T.bg2 }}>
        <p style={{ fontSize: 13, color: T.text3 }}>
          Есть идея или нашли баг?{' '}
          <a href="mailto:support@mybst.ru" style={{ color: T.accent, fontWeight: 600 }}>Напишите нам</a>
        </p>
      </footer>
    </div>
  )
}
